import React from "react";
import {
  Autocomplete,
  TextField,
  FormHelperText,
  CircularProgress,
  Typography,
} from "@mui/material";
import { InputField } from "./Inputs";
import { useDispatch, useSelector } from "react-redux";
import {
  setNotInList,
  removeNotInList,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { RootState } from "../../services/redux/store";

interface Option {
  value: string | number;
  label: string | number | React.ReactNode;
}

interface AutoCompleteOptionsProps {
  options?: Array<Option | string>;
  label?: string;
  disabled?: boolean;
  value?: Option | string | number | null;
  isMandatory?: boolean;
  onChange: (value: Option | string | number | null, reason?: string) => void;
  helperText?: string;
  error?: boolean;
  loading?: boolean;
  placeholder?: string;
  onInputChange?: any;
  inputValue?: string;
  fieldProps?: any;
  form?: any;
  isNotList?: boolean;
  formikRef?: any;
  noOptionText?: string;
}

const AutoCompleteOptions: React.FC<AutoCompleteOptionsProps> = ({
  options = [],
  label,
  disabled = false,
  value = null,
  isMandatory = false,
  onChange,
  helperText,
  error = false,
  loading = false,
  placeholder = "Select...",
  inputValue = "",
  onInputChange,
  fieldProps,
  form,
  isNotList = false,
  formikRef,
  noOptionText,
}) => {
  const notInList = useSelector(
    (state: RootState) => state?.stepper?.notInList
  );
  const [localInputValue, setLocalInputValue] = React.useState("");
  const [autoCompleteInput, setAutoCompleteInput] = React.useState(""); // New state to track what is typed inside the Autocomplete specifically
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout>();
  const formValuesRef = React.useRef<any>();
  const dispatch = useDispatch();

  const normalizedValue = React.useMemo(() => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "object") {
      return value as Option;
    }

    const primitive = String(value).toLowerCase();
    const matchedOption = (options || []).find((opt) => {
      if (typeof opt === "string") {
        return opt.toLowerCase() === primitive;
      }

      const optionValue = String(opt?.value ?? "").toLowerCase();
      const optionLabel = String(opt?.label ?? "").toLowerCase();
      return optionValue === primitive || optionLabel === primitive;
    });

    return (
      matchedOption || {
        value: String(value),
        label: String(value),
      }
    );
  }, [value, options]);

  const labelId = `autocomplete-label-${label
    ?.replace(/\s+/g, "-")
    .toLowerCase()}`;

  // Store form values in ref to avoid unnecessary re-renders
  React.useEffect(() => {
    if (form?.values) {
      formValuesRef.current = form.values;
    }
  });

  const handleNotInListClick = React.useCallback(
    (value: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Clear the current field value and local state
      form.setFieldValue(fieldProps.name, "");
      setLocalInputValue("");
      setAutoCompleteInput(""); // clear tracked input
      form.setFieldTouched(fieldProps.name, false);

      // Clear the autocomplete value via onChange callback
      onChange(null);

      const isAddingToNotInList = !notInList?.includes(value);

      if (isAddingToNotInList) {
        // Switching to "Not in list" mode
        dispatch(setNotInList(value));

        // If university is set to "Not in list", also set institute and course as "Not in list"
        if (fieldProps?.name.toLowerCase().includes("university")) {
          const instituteField = label?.replace("University", "Institute");
          const courseField = label?.replace("University", "Course");

          // Always clear institute and course values first
          form.setFieldValue("institute", "");
          form.setFieldValue("courseName", "");
          form.setFieldTouched("institute", false);
          form.setFieldTouched("courseName", false);

          // Then set them to "Not in list"
          if (instituteField && !notInList?.includes(instituteField)) {
            dispatch(setNotInList(instituteField));
          }
          if (courseField && !notInList?.includes(courseField)) {
            dispatch(setNotInList(courseField));
          }
        }

        // If institute is set to "Not in list", also set course as "Not in list"
        if (fieldProps?.name.toLowerCase().includes("institute")) {
          const courseField = label?.replace("Institute", "Course");

          // Always clear course value first
          form.setFieldValue("courseName", "");
          form.setFieldTouched("courseName", false);

          // Then set it to "Not in list"
          if (courseField && !notInList?.includes(courseField)) {
            dispatch(setNotInList(courseField));
          }
        }
      } else {
        // Switching back to "In list" mode (dropdown)
        dispatch(removeNotInList(value));

        // If university is set back to "In list", also revert institute and course to "In list"
        if (fieldProps?.name.toLowerCase().includes("university")) {
          const instituteField = label?.replace("University", "Institute");
          const courseField = label?.replace("University", "Course");

          if (instituteField && notInList?.includes(instituteField)) {
            dispatch(removeNotInList(instituteField));
          }
          if (courseField && notInList?.includes(courseField)) {
            dispatch(removeNotInList(courseField));
          }

          // Clear institute and course values to remove any error messages
          form.setFieldValue("institute", "");
          form.setFieldValue("courseName", "");

          // Clear touched state to remove validation errors
          form.setFieldTouched("institute", false);
          form.setFieldTouched("courseName", false);
        }

        // If institute is set back to "In list", also revert course to "In list"
        if (fieldProps?.name.toLowerCase().includes("institute")) {
          const courseField = label?.replace("Institute", "Course");

          if (courseField && notInList?.includes(courseField)) {
            dispatch(removeNotInList(courseField));
          }

          // Clear course value to remove any error messages
          form.setFieldValue("courseName", "");

          // Clear touched state to remove validation errors
          form.setFieldTouched("courseName", false);
        }
      }
    },
    [form, fieldProps?.name, notInList, dispatch, label, onChange]
  );

  // Debounced function to update form value
  const debouncedFormUpdate = React.useCallback(
    (value: string) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(() => {
        if (form && fieldProps?.name) {
          form.setFieldValue(fieldProps.name, value);
        }
      }, 300); // 300ms delay
    },
    [form, fieldProps?.name]
  );

  // Handle input change with local state and debounced form update
  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalInputValue(newValue);
      debouncedFormUpdate(newValue);
    },
    [debouncedFormUpdate]
  );

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Initialize local input value when field value changes - only when necessary
  React.useEffect(() => {
    if (form && fieldProps?.name) {
      const fieldValue = form.values[fieldProps.name];
      // Handle both filled and empty values
      if (typeof fieldValue === "string" && fieldValue !== localInputValue) {
        setLocalInputValue(fieldValue);
      } else if (!fieldValue && localInputValue) {
        // If field value is empty/null but localInputValue has content, clear it
        setLocalInputValue("");
      }
    }
  }, [fieldProps?.name, form?.values?.[fieldProps?.name]]); // Watch the specific field value

  return (
    <div className="form-container">
      {/* Label */}
      <div>
        <label
          id={labelId}
          className="text-lable"
          htmlFor="autocomplete-input"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {label} { (isMandatory || label?.toLowerCase() === "designation" || label?.toLowerCase() === "country") && <span style={{ color: "red" }}>*</span> }
          </span>
          {isNotList && (
            <Typography
              sx={{
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "500",
                color: "rgba(0, 179, 181, 1)",
                "&:hover": {
                  color: "rgba(0, 150, 152, 1)",
                },
              }}
              onClick={(event) => handleNotInListClick(label || "", event)}
            >
              {notInList?.includes(label || "") ? "In list" : "Not found? Click here"}
            </Typography>
          )}
        </label>
      </div>

      {isNotList && notInList?.includes(label || "") ? (
        <InputField
          {...fieldProps}
          fullWidth
          // placeholder={`Enter ${label?.toLowerCase() || "value"}`}
          variant="outlined"
          error={false}
          helperText={""}
          value={localInputValue}
          onKeyDown={(e) => {
            // Block leading space: allow spaces only after first character
            if (e.key === " ") {
              const target = e.target as HTMLInputElement;
              const start = target.selectionStart ?? 0;
              if (start === 0) {
                e.preventDefault();
              }
            }
          }}
          onChange={handleInputChange}
          disabled={disabled}
        />
      ) : (
        <Autocomplete
          noOptionsText={
            autoCompleteInput?.length > 0 ? noOptionText : placeholder
          }
          onInputChange={(event, newInputValue, reason) => {
            setAutoCompleteInput(newInputValue);
            onInputChange?.(event, newInputValue, reason);
          }}
          id="autocomplete-input"
          value={normalizedValue}
          options={options}
          disabled={disabled}
          onChange={(_, newValue, reason) => onChange(newValue, reason)}
          getOptionLabel={(option) => {
            if (!option) return "";
            if (typeof option === "string") return option;
            return option.label !== undefined ? String(option.label) : "";
          }}
          isOptionEqualToValue={(option, val) => {
            if (!option || !val) return false;

            const optionValue =
              typeof option === "string"
                ? option.toLowerCase()
                : String((option as any).value ?? "").toLowerCase();
            const optionLabel =
              typeof option === "string"
                ? option.toLowerCase()
                : String((option as any).label ?? "").toLowerCase();

            if (typeof val !== "object") {
              const primitive = String(val ?? "").toLowerCase();
              return optionValue === primitive || optionLabel === primitive;
            }

            const valValue = String((val as any).value ?? "").toLowerCase();
            const valLabel = String((val as any).label ?? "").toLowerCase();
            return optionValue === valValue || optionLabel === valLabel;
          }}
          loading={loading}
          popupIcon={loading ? <CircularProgress size={16} /> : undefined}
          fullWidth
          size="small"
          sx={{
            textTransform: "capitalize !important",
            "& .MuiOutlinedInput-root": {
              borderRadius: "6px",
              height: "38px",
              paddingRight: "8px !important",
              "& fieldset": {
                borderColor: error ? "#d32f2f" : "default",
              },
              "&:hover fieldset": {
                borderColor: error ? "#d32f2f" : "#1CB1B1",
              },
              "&.Mui-focused fieldset": {
                borderColor: error ? "#d32f2f" : "#1CB1B1",
              },
            },
            "& .MuiAutocomplete-input": {
              padding: "0px 6px !important",
              fontSize: "11px",
              color: "#a5a3ae",
              fontWeight: "400",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              placeholder={placeholder}
              variant="outlined"
            />
          )}
        />
      )}

      {/* Helper Text */}
      {helperText && (
        <FormHelperText
          error={error}
          style={{ marginTop: "4px", fontSize: "11px" }}
        >
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default AutoCompleteOptions;
